import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import AuthenticatedLayout from './App';
import { AuthCallbackPage } from './pages/auth/callback';
import { RedirectHandler } from './pages/redirect';
import { FeatureErrorBoundary } from './components/FeatureErrorBoundary';

export interface RouteMeta {
  title?: string;
  breadcrumb?: string;
  requiresAuth?: boolean;
}

type AppRoute = RouteObject & {
  meta?: RouteMeta;
};

// Lazy load pages
const LandingPage = lazy(() => import('./pages/landing'));
const LoginPage = lazy(() => import('./pages/login'));
const SignUpPage = lazy(() => import('./pages/signup'));
const DashboardPage = lazy(() => import('./pages/dashboard/dashboardV2'));
const SettingsPage = lazy(() => import('./pages/settings'));
const ChatPage = lazy(() => import('./pages/chat'));
const TaskExamplePage = lazy(() => import('./pages/examples/TaskExample'));
const CalendarFlowExample = lazy(
  () => import('./pages/examples/CalendarFlowExample')
);
const PomodoroPage = lazy(() => import('./pages/pomodoro'));
const PoliciesPage = lazy(() => import('./pages/policies'));
const PrivacyPolicyPage = lazy(() => import('./pages/policies/privacy-policy'));
const TermsAndConditionsPage = lazy(
  () => import('./pages/policies/terms-and-conditions')
);
const SupportPage = lazy(() => import('./pages/support'));
const BlogListPage = lazy(() => import('./pages/blog/BlogList'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPost'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const LinkResolverPage = lazy(() => import('./pages/LinkResolverPage'));
const MeetingRequestPage = lazy(
  () => import('./pages/event/MeetingRequestPage')
);
const FeaturesListPage = lazy(() => import('./pages/features/FeaturesList'));
const FeatureDetailPage = lazy(() => import('./pages/features/FeatureDetail'));
const KeyshaMixPage = lazy(() => import('./pages/mix/KeyshaMix'));
const TestFlightPage = lazy(() => import('./pages/testflight'));

// Shared fallback for suspense
const LoadingFallback = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgb(26, 26, 46)',
      color: '#F0F3FF',
      padding: '2rem',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
    }}
  >
    <div
      style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(109, 83, 159, 0.3)',
        borderTop: '3px solid #6D539F',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '2rem',
      }}
    />
    <div
      style={{
        fontSize: '1.25rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        textAlign: 'center',
      }}
    >
      Loading Keysha...
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Error boundary wrapper for RedirectHandler
const RedirectHandlerWithErrorBoundary = () => (
  <FeatureErrorBoundary
    fallback={
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgb(26, 26, 46)',
          color: '#F0F3FF',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ color: '#ff6b6b', marginBottom: '1rem' }}>
          Unable to Process Redirect
        </h2>
        <p style={{ color: '#9C9C9D', marginBottom: '2rem' }}>
          We encountered an error while processing your redirect.
        </p>
        <a
          href="/"
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #6D539F, #3B82F6)',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '500',
          }}
        >
          Return to Home
        </a>
      </div>
    }
  >
    <RedirectHandler />
  </FeatureErrorBoundary>
);

// Default error route
export const errorElement = <Navigate to="/" replace />;

// Router config with meta support and v7 future flags
export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LandingPage />
        </Suspense>
      ),
      meta: { title: 'Home', requiresAuth: false },
      errorElement,
    },
    {
      path: '/login',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      ),
      meta: { title: 'Login', requiresAuth: false },
    },
    {
      path: '/signup',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <SignUpPage />
        </Suspense>
      ),
      meta: { title: 'Sign Up', requiresAuth: false },
    },
    {
      path: '/auth/callback',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <AuthCallbackPage />
        </Suspense>
      ),
    },
    // Universal redirect routes - semantic URLs for easy sharing
    {
      path: '/upgrade',
      element: <RedirectHandlerWithErrorBoundary />,
      meta: { title: 'Upgrade Your Plan', requiresAuth: false },
    },
    {
      path: '/pay',
      element: <RedirectHandlerWithErrorBoundary />,
      meta: { title: 'Complete Payment', requiresAuth: false },
    },
    {
      path: '/join',
      element: <RedirectHandlerWithErrorBoundary />,
      meta: { title: 'Join Keysha', requiresAuth: false },
    },
    {
      path: '/verify',
      element: <RedirectHandlerWithErrorBoundary />,
      meta: { title: 'Verify Your Phone', requiresAuth: false },
    },
    {
      path: '/r',
      element: <RedirectHandlerWithErrorBoundary />,
      meta: { title: 'Redirecting...', requiresAuth: false },
    },
    {
      path: '/policies',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PoliciesPage />
        </Suspense>
      ),
      meta: { title: 'Policies', requiresAuth: false },
    },
    {
      path: '/policies/privacy-policy',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PrivacyPolicyPage />
        </Suspense>
      ),
      meta: { title: 'Privacy Policy', requiresAuth: false },
    },
    {
      path: '/policies/terms-and-conditions',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <TermsAndConditionsPage />
        </Suspense>
      ),
      meta: { title: 'Terms & Conditions', requiresAuth: false },
    },
    // Support/Contact page (public)
    {
      path: '/support',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <SupportPage />
        </Suspense>
      ),
      meta: { title: 'Contact & Support', requiresAuth: false },
    },
    {
      path: '/contact',
      element: <Navigate to="/support" replace />,
    },
    // Blog routes (public)
    {
      path: '/blog',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <BlogListPage />
        </Suspense>
      ),
      meta: { title: 'Blog', requiresAuth: false },
    },
    {
      path: '/blog/:slug',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <BlogPostPage />
        </Suspense>
      ),
      meta: { title: 'Blog Post', requiresAuth: false },
    },

    // Pomodoro Timer (public)
    {
      path: '/pomodoro',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PomodoroPage />
        </Suspense>
      ),
      meta: {
        title: 'Focus Flow - Pomodoro Timer',
        breadcrumb: 'Pomodoro',
        requiresAuth: false,
      },
    },
    // Features routes (public)
    {
      path: '/features',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <FeaturesListPage />
        </Suspense>
      ),
      meta: { title: 'Features', requiresAuth: false },
    },
    {
      path: '/features/:slug',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <FeatureDetailPage />
        </Suspense>
      ),
      meta: { title: 'Feature', requiresAuth: false },
    },
    // Mix - Event Networking (public)
    {
      path: '/mix',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <KeyshaMixPage />
        </Suspense>
      ),
      meta: {
        title: 'Mix - Event Networking by Keysha.ai',
        requiresAuth: false,
      },
    },
    {
      element: <AuthenticatedLayout />,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/dashboard',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <DashboardPage />
            </Suspense>
          ),
          meta: {
            title: 'Dashboard',
            breadcrumb: 'Dashboard',
            requiresAuth: true,
          },
        },
        {
          path: '/settings',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SettingsPage />
            </Suspense>
          ),
          meta: {
            title: 'Settings',
            breadcrumb: 'Settings',
            requiresAuth: true,
          },
        },
        {
          path: '/chat',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ChatPage />
            </Suspense>
          ),
          meta: {
            title: 'Chat with Keysha',
            breadcrumb: 'Chat',
            requiresAuth: true,
          },
        },
        {
          path: '/tasks',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TaskExamplePage />
            </Suspense>
          ),
          meta: { title: 'Tasks', breadcrumb: 'Tasks', requiresAuth: true },
        },
        {
          path: '/calendar-flow',
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CalendarFlowExample />
            </Suspense>
          ),
          meta: { title: 'Tasks', breadcrumb: 'Tasks', requiresAuth: true },
        },
      ],
      errorElement,
    },
    // Book Meeting Page (public)
    {
      path: '/event/:eventSlug/:attendeeId',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <MeetingRequestPage />
        </Suspense>
      ),
      meta: { title: 'Meeting Request', requiresAuth: false },
      errorElement,
    },
    // Link Resolver (public)
    {
      path: '/p/:token',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <LinkResolverPage />
        </Suspense>
      ),
      meta: { title: 'Redirecting...', requiresAuth: false },
      errorElement,
    },
    // TestFlight Landing Page (public)
    {
      path: '/testflight',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <TestFlightPage />
        </Suspense>
      ),
      meta: { title: 'Join TestFlight - Keysha', requiresAuth: false },
      errorElement,
    },
    {
      path: '/:username',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <ProfilePage />
        </Suspense>
      ),
      meta: { title: 'Book Meeting', requiresAuth: false },
      errorElement,
    },
    {
      path: '*',
      element: <Navigate to="/" replace />,
    },
  ] as AppRoute[],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  }
);

// TIPS
// Later, you can use useMatches() to read this metadata dynamically, for example:
// import { useMatches } from 'react-router-dom';

// export const PageTitle = () => {
//   const matches = useMatches();
//   const meta = matches[matches.length - 1]?.route?.meta;
//   return <h1>{meta?.title || 'App'}</h1>;
// };
